/**
 * Haptic Feedback Utility
 * Provides tactile feedback on supported devices
 */

import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';
import { Capacitor } from '@capacitor/core';

const isNative = Capacitor.isNativePlatform();

// Global haptics enabled state (can be toggled by user)
let hapticsEnabled = true;

/**
 * Set haptics enabled/disabled
 */
export const setHapticsEnabled = (enabled) => {
  hapticsEnabled = enabled;
};

/**
 * Get current haptics enabled state
 */
export const getHapticsEnabled = () => hapticsEnabled;

/**
 * Light tap - for UI interactions (button presses, toggles)
 */
export const tapLight = async () => {
  if (!isNative || !hapticsEnabled) return;
  try {
    await Haptics.impact({ style: ImpactStyle.Light });
  } catch (e) {
    console.warn('Haptics tapLight failed:', e);
  }
};

/**
 * Medium tap - for confirmations (barcode detected, action completed)
 */
export const tapMedium = async () => {
  if (!isNative || !hapticsEnabled) return;
  try {
    await Haptics.impact({ style: ImpactStyle.Medium });
  } catch (e) {
    console.warn('Haptics tapMedium failed:', e);
  }
};

/**
 * Heavy tap - for important events
 */
export const tapHeavy = async () => {
  if (!isNative || !hapticsEnabled) return;
  try {
    await Haptics.impact({ style: ImpactStyle.Heavy });
  } catch (e) {
    console.warn('Haptics tapHeavy failed:', e);
  }
};

/**
 * Success notification - for halal confirmation
 */
export const notifySuccess = async () => {
  if (!isNative || !hapticsEnabled) return;
  try {
    await Haptics.notification({ type: NotificationType.Success });
  } catch (e) {
    // Haptics not available
  }
};

/**
 * Warning notification - for doubtful status
 */
export const notifyWarning = async () => {
  if (!isNative || !hapticsEnabled) return;
  try {
    await Haptics.notification({ type: NotificationType.Warning });
  } catch (e) {
    // Haptics not available
  }
};

/**
 * Error notification - for haram detection
 */
export const notifyError = async () => {
  if (!isNative || !hapticsEnabled) return;
  try {
    await Haptics.notification({ type: NotificationType.Error });
  } catch (e) {
    // Haptics not available
  }
};

/**
 * Vibrate with custom duration (ms)
 */
export const vibrate = async (duration = 100) => {
  if (!isNative || !hapticsEnabled) return;
  try {
    await Haptics.vibrate({ duration });
  } catch (e) {
    // Haptics not available
  }
};

/**
 * Status-based haptic feedback
 * @param {'halal' | 'haram' | 'doubtful'} status 
 */
export const notifyStatus = async (status) => {
  switch (status) {
    case 'halal':
      return notifySuccess();
    case 'haram':
      return notifyError();
    case 'doubtful':
      return notifyWarning();
    default:
      return tapLight();
  }
};
